import * as Pages from "@/components/Pages";
import * as Paper from "@/components/PaperContainer";
import * as PageOptions from "@/components/PaperContainer/PageOptions";
import * as Discussions from "@/models/discussions";
import * as ReactionsModel from "@/models/reactions";
import * as React from "react";

import { CommentSection, useComments } from "@/features/CommentSection";

import { useCurrentSubscriptionsAdapter } from "@/models/subscriptions";
import { compareIds } from "@/routes/paths";

import { useMe } from "@/contexts/CurrentCompanyContext";
import { useBoolState } from "@/hooks/useBoolState";
import { useFormattedTimePreferences } from "@/hooks/useFormattedTimePreferences";
import { useClearNotificationsOnLoad } from "@/features/notifications";
import { assertPresent } from "@/utils/assertions";
import { useNavigate } from "react-router";
import { useLoadedData } from "./loader";
import {
  DiscardDiscussionDraftModal,
  DocumentTitle,
  IconEdit,
  IconTrash,
  OngoingDraftActions,
  Reactions,
  RichContent,
  CurrentSubscriptions,
  Spacer,
  displayDate,
} from "turboui";

import { useRichEditorHandlers } from "@/hooks/useRichEditorHandlers";
import { usePaths } from "@/routes/paths";

export function Page() {
  const { discussion } = useLoadedData();

  assertPresent(discussion.notifications, "Discussion notifications must be defined");
  useClearNotificationsOnLoad(discussion.notifications);

  return (
    <Pages.Page title={discussion.title!} testId="discussion-page">
      <Paper.Root size="medium">
        <Navigation space={discussion.space} />

        <Paper.Body minHeight="600px" className="lg:px-28">
          <Options />
          <ContinueEditingDraft />
          <DiscussionTitle />
          <DiscussionBody />
          <DiscussionReactions />
          <DicusssionComments />

          {discussion.state === "published" && <DiscussionSubscriptions />}
        </Paper.Body>
      </Paper.Root>
    </Pages.Page>
  );
}

function DiscussionBody() {
  const { discussion } = useLoadedData();
  const { mentionedPersonLookup } = useRichEditorHandlers();

  return (
    <>
      <Spacer size={4} />
      <RichContent
        content={discussion.body}
        className="text-md sm:text-lg"
        mentionedPersonLookup={mentionedPersonLookup}
        parseContent
      />
    </>
  );
}

function DiscussionSubscriptions() {
  const { discussion, isCurrentUserSubscribed } = useLoadedData();
  const refresh = Pages.useRefresh();

  if (!discussion.potentialSubscribers || !discussion.subscriptionList) {
    return null;
  }

  return (
    <DiscussionSubscriptionsContent
      discussion={discussion}
      isCurrentUserSubscribed={isCurrentUserSubscribed}
      onRefresh={refresh}
    />
  );
}

function DiscussionSubscriptionsContent({
  discussion,
  isCurrentUserSubscribed,
  onRefresh,
}: {
  discussion: Discussions.Discussion;
  isCurrentUserSubscribed: boolean;
  onRefresh: () => void;
}) {
  assertPresent(discussion.potentialSubscribers, "potentialSubscribers must be present in discussion");
  assertPresent(discussion.subscriptionList, "subscriptionList must be present in discussion");

  const subscriptionsState = useCurrentSubscriptionsAdapter({
    potentialSubscribers: discussion.potentialSubscribers,
    subscriptionList: discussion.subscriptionList,
    resourceName: "discussion",
    type: "message",
    onRefresh,
  });

  return (
    <>
      <div className="border-t border-stroke-base mt-16 mb-8" />

      <CurrentSubscriptions
        {...subscriptionsState}
        isCurrentUserSubscribed={isCurrentUserSubscribed}
        canEditSubscribers={discussion.permissions?.canEdit || false}
      />
    </>
  );
}

function DiscussionReactions() {
  const { discussion } = useLoadedData();
  const reactions = (discussion.reactions ?? []).map((r) => r!);
  const entity = ReactionsModel.entity(discussion.id!, "message");
  const form = ReactionsModel.useReactionsForm(entity, reactions);

  if (discussion.state !== "published") return null;

  assertPresent(discussion.permissions?.canComment, "permissions must be present in discussion");

  return (
    <>
      <Spacer size={2} />
      <Reactions {...form} size={24} canAddReaction={discussion.permissions.canComment} />
    </>
  );
}

function DiscussionTitle() {
  const { discussion } = useLoadedData();
  const formattedTimePreferences = useFormattedTimePreferences();

  return (
    <DocumentTitle
      title={discussion.title}
      author={discussion.author || null}
      state={discussion.state}
      publishedAt={displayDate(discussion)}
      scheduledAt={discussion.scheduledAt}
      formattedTimePreferences={formattedTimePreferences}
    />
  );
}

function Navigation({ space }) {
  const paths = usePaths();
  return (
    <Paper.Navigation
      items={[
        { to: paths.spacePath(space.id), label: space.name },
        { to: paths.spaceDiscussionsPath(space.id), label: "Discussions" },
      ]}
    />
  );
}

function Options() {
  const paths = usePaths();
  const me = useMe();
  const navigate = useNavigate();
  const { discussion } = useLoadedData();
  const [archive] = Discussions.useArchiveMessage();
  const [showDiscardModal, toggleDiscardModal] = useBoolState(false);

  const isUnpublished = discussion.state === "draft" || discussion.state === "scheduled";

  const handleArchive = async () => {
    await archive({ id: discussion.id });

    handleRedirect();
  };

  const handleRedirect = () => {
    if (discussion.space) {
      navigate(paths.spaceDiscussionsPath(discussion.space.id));
    } else {
      navigate(paths.homePath());
    }
  };

  if (!discussion.author || !me || !compareIds(me.id, discussion.author.id)) return null;

  return (
    <>
      <PageOptions.Root testId="options-button">
        <PageOptions.Link
          icon={IconEdit}
          title="Edit"
          to={paths.discussionEditPath(discussion.id!)}
          testId="edit-discussion"
          keepOutsideOnBigScreen
        />

        {isUnpublished ? (
          <PageOptions.Action
            icon={IconTrash}
            title="Discard draft"
            onClick={toggleDiscardModal}
            testId="discard-draft"
          />
        ) : (
          <PageOptions.Action icon={IconTrash} title="Delete" onClick={handleArchive} testId="archive-discussion" />
        )}
      </PageOptions.Root>

      {isUnpublished && (
        <DiscardDiscussionDraftModal
          isOpen={showDiscardModal}
          onClose={toggleDiscardModal}
          onDiscard={() => archive({ id: discussion.id })}
          onSuccess={handleRedirect}
        />
      )}
    </>
  );
}

function DicusssionComments() {
  const { discussion } = useLoadedData();
  const commentsForm = useComments({ discussion: discussion, parentType: "message" });

  if (discussion.state !== "published") return null;

  assertPresent(discussion.permissions?.canComment, "permissions must be present in discussion");

  return (
    <>
      <Spacer size={4} />
      <div className="border-t border-stroke-base mt-8" />
      <CommentSection form={commentsForm} commentParentType="message" canComment={discussion.permissions.canComment} />
    </>
  );
}

function ContinueEditingDraft() {
  const paths = usePaths();
  const { discussion } = useLoadedData();
  const formattedTimePreferences = useFormattedTimePreferences();

  const [publish] = Discussions.usePublishDiscussion();
  const refresh = Pages.useRefresh();
  const editPath = paths.discussionEditPath(discussion.id!);

  if (discussion.state !== "draft" && discussion.state !== "scheduled") {
    return null;
  }

  const publishHandler = async () => {
    await publish({ id: discussion.id! });
    refresh();
  };

  return (
    <OngoingDraftActions
      state={discussion.state}
      updatedAt={discussion.updatedAt!}
      scheduledAt={discussion.scheduledAt}
      editPath={editPath}
      onPublish={publishHandler}
      formattedTimePreferences={formattedTimePreferences}
    />
  );
}
